import { useEffect, useState } from "react";
import { Activity, AlertTriangle, ChevronRight, Scale, Users } from "lucide-react";
import { http, notify, roleLabels, money } from "../api";
import { Loading, Modal } from "./common";

/**
 * Team roster — one card per member showing load, activity and conversion.
 * Clicking a member navigates to Leads pre-filtered to their pipeline.
 * Managers can select telecallers and rebalance active leads across them.
 */
export default function TeamRoster({ onOpenMember }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/roster")
      .then((r) => setData(r.data))
      .catch((err) => notify.error(err, "Could not load team roster"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Loading>Assembling roster…</Loading>;
  if (!data || !data.teams.length) {
    return (
      <div className="page-intro">
        <div>
          <p className="eyebrow">PEOPLE / HIERARCHY</p>
          <h1>Team roster</h1>
          <p className="muted">No team is assigned to your account yet.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">PEOPLE / HIERARCHY</p>
          <h1>Team roster</h1>
          <p className="muted">Rebalance workload without opening a single record.</p>
        </div>
      </div>

      {data.teams.map((t) => (
        <TeamSection key={t.team_id} team={t} onOpenMember={onOpenMember} reload={load} />
      ))}
    </>
  );
}

function TeamSection({ team, onOpenMember, reload }) {
  const [picked, setPicked] = useState(new Set());
  const [rebalanceOpen, setRebalanceOpen] = useState(false);
  const maxLoad = Math.max(1, ...team.members.map((m) => m.load.active_leads));
  const maxActivity = Math.max(1, ...team.members.map((m) => m.activity.actions_last_7d));
  const eligible = team.members.filter((m) => m.role === "telecaller" && m.active);

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const canRebalance = team.team_type === "sales" && eligible.length >= 2;

  return (
    <section className="roster-team" data-testid={`roster-team-${team.team_id}`}>
      <header className="roster-team-head">
        <div>
          <p className="eyebrow">
            {team.team_type === "back_office" ? "BACK OFFICE" : "SALES"} · {team.members.length} people
            {team.idle_count > 0 && (
              <span className="idle-summary" data-testid={`roster-idle-summary-${team.team_id}`}>
                {" "}· {team.idle_count} idle
              </span>
            )}
          </p>
          <h2>{team.team_name}</h2>
          <p className="muted">
            Manager: <strong>{team.manager_name || "Unassigned"}</strong>
          </p>
        </div>
        <div className="roster-totals">
          <div>
            <span>Active leads</span>
            <strong data-testid={`roster-total-active-${team.team_id}`}>{team.totals.active_leads}</strong>
          </div>
          <div><span>Applications</span><strong>{team.totals.applications}</strong></div>
          <div><span>Approved</span><strong>{team.totals.approved}</strong></div>
          <div><span>7-day activity</span><strong>{team.totals.actions_last_7d}</strong></div>
          {canRebalance && (
            <button
              className="primary-button compact"
              onClick={() => setRebalanceOpen(true)}
              data-testid={`roster-rebalance-${team.team_id}`}
              disabled={picked.size < 2}
              title={picked.size < 2 ? "Select at least two telecallers to rebalance" : "Rebalance selected"}
            >
              <Scale size={15} /> Rebalance {picked.size >= 2 ? `(${picked.size})` : ""}
            </button>
          )}
        </div>
      </header>

      <div className="roster-grid">
        {team.members.map((m) => (
          <MemberCard
            key={m.id}
            member={m}
            maxLoad={maxLoad}
            maxActivity={maxActivity}
            picked={picked.has(m.id)}
            onPick={m.role === "telecaller" ? () => togglePick(m.id) : null}
            onOpen={() => onOpenMember?.(m.id, m.name)}
          />
        ))}
      </div>

      {rebalanceOpen && (
        <RebalanceModal
          teamName={team.team_name}
          memberIds={[...picked]}
          onClose={() => setRebalanceOpen(false)}
          onApplied={() => {
            setRebalanceOpen(false);
            setPicked(new Set());
            reload();
          }}
        />
      )}
    </section>
  );
}

function MemberCard({ member, maxLoad, maxActivity, onOpen, picked, onPick }) {
  const l = member.load;
  const a = member.activity;
  const c = member.conversion;
  const loadPct = Math.round((l.active_leads / maxLoad) * 100);
  const actPct = Math.round((a.actions_last_7d / maxActivity) * 100);
  const isManager = member.role_label === "Team lead";
  const overloaded = l.active_leads > 0 && l.overdue_followups > 2;
  const isIdle = a.is_idle;

  const lastAction = a.last_action_at ? relativeTime(a.last_action_at) : "Never";
  const idleLabel = a.idle_working_days >= 999
    ? "never active"
    : `${a.idle_working_days} working ${a.idle_working_days === 1 ? "day" : "days"} idle`;

  const cardClass = [
    "roster-card",
    isManager && "roster-card--manager",
    !member.active && "roster-card--inactive",
    isIdle && "roster-card--idle",
    picked && "roster-card--picked",
  ].filter(Boolean).join(" ");

  return (
    <article
      data-testid={`roster-member-${member.id}`}
      className={cardClass}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen?.()}
    >
      <header className="roster-card-head">
        {onPick && (
          <input
            type="checkbox"
            className="roster-pick"
            checked={picked}
            onClick={(e) => e.stopPropagation()}
            onChange={onPick}
            aria-label={`Pick ${member.name} for rebalance`}
            data-testid={`roster-pick-${member.id}`}
          />
        )}
        <div className="avatar large">{member.name?.slice(0, 2).toUpperCase()}</div>
        <div>
          <strong>{member.name}</strong>
          <small>{member.role_label} · {roleLabels[member.role] || member.role}</small>
        </div>
        {isIdle && (
          <span className="idle-flag" data-testid={`roster-idle-${member.id}`} title={idleLabel}>
            <AlertTriangle size={13} /> IDLE
          </span>
        )}
        {overloaded && !isIdle && (
          <span className="overload-flag" title="Multiple follow-ups overdue">
            <AlertTriangle size={14} /> heavy
          </span>
        )}
        {!member.active && <span className="inactive-flag">Inactive</span>}
      </header>

      <div className="roster-metric-row">
        <div className="roster-metric">
          <span className="metric-label">Active load</span>
          <strong>{l.active_leads}</strong>
          <div className="roster-bar"><i style={{ width: `${loadPct}%` }} /></div>
        </div>
        <div className="roster-metric">
          <span className="metric-label">7-day activity</span>
          <strong>{a.actions_last_7d}</strong>
          <div className="roster-bar activity"><i style={{ width: `${actPct}%` }} /></div>
        </div>
      </div>

      <div className="roster-fact-strip">
        <span title="Today's follow-ups"><Activity size={12} /> {l.todays_followups} today</span>
        <span title="Overdue follow-ups" className={l.overdue_followups ? "warn" : ""}>
          {l.overdue_followups} overdue
        </span>
        <span title="Documents pending">{l.documents_pending} docs</span>
      </div>

      <div className="roster-conversion">
        <div>
          <small>Lead → App</small>
          <strong>{c.lead_to_app_pct}%</strong>
          <span className="muted">{c.applications}/{c.total_leads}</span>
        </div>
        <div>
          <small>Approval</small>
          <strong>{c.approval_pct}%</strong>
          <span className="muted">{c.approved}/{c.applications || 0}</span>
        </div>
        <div>
          <small>Disbursed</small>
          <strong>{c.disbursed}</strong>
          <span className="muted">total</span>
        </div>
      </div>

      <footer className="roster-card-foot">
        <span className="muted"><Users size={12} /> Last action: {lastAction}</span>
        <ChevronRight size={15} />
      </footer>
    </article>
  );
}

function RebalanceModal({ teamName, memberIds, onClose, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    http.post("/roster/rebalance", { member_ids: memberIds, apply: false })
      .then((r) => setPreview(r.data))
      .catch((err) => notify.error(err, "Could not compute a plan"))
      .finally(() => setLoading(false));
  }, [memberIds]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await http.post("/roster/rebalance", { member_ids: memberIds, apply: true });
      notify.success(`Rebalance complete — ${r.data.moves || 0} lead${r.data.moves === 1 ? "" : "s"} moved`);
      onApplied();
    } catch (err) {
      notify.error(err, "Rebalance failed");
      setApplying(false);
    }
  };

  return (
    <Modal title={`Rebalance ${teamName}`} onClose={onClose}>
      <div className="record-form">
        {loading || !preview ? (
          <Loading>Computing the fairest split…</Loading>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              {preview.total} active lead{preview.total === 1 ? "" : "s"} · target{" "}
              <strong>{preview.target_min}–{preview.target_max}</strong> per teammate.
            </p>

            <div className="rebalance-summary">
              {preview.members.map((m) => (
                <div key={m.id} className="rebalance-member" data-testid={`rebalance-summary-${m.id}`}>
                  <strong>{m.name}</strong>
                  <span className="rebalance-arrow">
                    <span className={m.before > m.after ? "shrinks" : m.before < m.after ? "grows" : ""}>
                      {m.before}
                    </span>
                    <em>→</em>
                    <span className={m.before !== m.after ? "highlight" : ""}>{m.after}</span>
                  </span>
                </div>
              ))}
            </div>

            {preview.plan.length === 0 ? (
              <div className="empty compact-empty">
                Everyone is already within one lead of the fair share. No moves needed.
              </div>
            ) : (
              <div className="rebalance-plan" data-testid="rebalance-plan">
                <p className="eyebrow" style={{ marginBottom: 8 }}>
                  {preview.plan.length} MOVE{preview.plan.length === 1 ? "" : "S"} · stalest leads first
                </p>
                <ul>
                  {preview.plan.map((p) => (
                    <li key={p.lead_id} data-testid={`rebalance-move-${p.lead_id}`}>
                      <span className="mono">{p.lead_id}</span>
                      <span>{p.customer_name || "Lead"}</span>
                      <span className="muted">{money(p.loan_amount)} · {p.status}</span>
                      <span className="move-arrow">
                        <b>{p.from_name}</b> → <b className="highlight">{p.to_name}</b>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="outline-button" onClick={onClose} disabled={applying}>
                Cancel
              </button>
              {preview.plan.length > 0 && (
                <button
                  type="button"
                  className="primary-button"
                  disabled={applying}
                  onClick={apply}
                  data-testid="rebalance-apply"
                >
                  {applying ? "Applying…" : `Apply ${preview.plan.length} move${preview.plan.length === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return `${months} mo ago`;
}
